import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getAssetUrl } from '../lib/supabaseClient';

export const Hero: React.FC = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [isMobileAuto, setIsMobileAuto] = useState(false);
  const [isPcInterference, setIsPcInterference] = useState(false);
  
  // NUEVO: Estado en tiempo real para saber en qué dispositivo estamos
  const [isMobile, setIsMobile] = useState(false);

  // 1. EFECTO: Detectar cambios de tamaño de pantalla en tiempo real
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.matchMedia('(max-width: 768px)').matches);
    checkMobile(); // Chequeo inicial
    
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 2. EFECTO: Manejo de los temporizadores (Reacciona a cambios en isMobile)
  useEffect(() => {
    // Frecuencia global: Ocurre cada 6 segundos
    const FRECUENCIA = 10000; 
    let timeoutId: NodeJS.Timeout;

    if (isMobile) {
      // --- MÓVIL ---
      const DURACION_MOVIL = 4000;
      
      const interval = setInterval(() => {
        setIsMobileAuto(true);
        timeoutId = setTimeout(() => {
          setIsMobileAuto(false);
        }, DURACION_MOVIL);
      }, FRECUENCIA);
      
      return () => {
        clearInterval(interval);
        clearTimeout(timeoutId);
        setIsMobileAuto(false); // Limpieza de seguridad al cambiar a PC
      };
      
    } else {
      // --- PC ---
      const DURACION_PC = 300; // 5 veces menos que en móvil
      
      const interval = setInterval(() => {
        setIsPcInterference(true);
        timeoutId = setTimeout(() => {
          setIsPcInterference(false);
        }, DURACION_PC); 
      }, FRECUENCIA);
      
      return () => {
        clearInterval(interval);
        clearTimeout(timeoutId);
        setIsPcInterference(false); // Limpieza de seguridad al cambiar a móvil
      };
    }
  }, [isMobile]); // <-- CRÍTICO: Se vuelve a ejecutar si isMobile cambia

  // Determina si se muestra la imagen digital
  const showDigital = isHovered || isMobileAuto || isPcInterference;
  
  // --- LÓGICA DE TRANSICIÓN ---
  // Si estamos en PC, no hay hover, y es el efecto automático -> Rápido (150ms)
  // En cualquier otro caso (Móvil automático, PC Hover) -> Lento (750ms)
  const isFastGlitch = isPcInterference && !isHovered && !isMobile;
  const transitionClass = isFastGlitch ? "duration-150" : "duration-[750ms]";

  // Velocidad de la vibración de colores CSS
  const animSpeedR = isFastGlitch ? "0.4s" : "2s";
  const animSpeedB = isFastGlitch ? "0.5s" : "2.5s";

  const realityImg = getAssetUrl(isMobile ? 'Image-Hero1-Mobile.png' : 'Image-Hero1.png');
  const digitalImg = getAssetUrl(isMobile ? 'Image-Hero2-Mobile.png' : 'Image-Hero2.png');

  return (
    <section id="inicio" className="relative min-h-[100svh] w-full flex items-center justify-center overflow-hidden bg-[#050505] px-4 pt-24 pb-14 md:pt-28">
      
      <style>{`
        .glitch-layer-r { 
          animation: glitch-anim-1 ${animSpeedR} infinite linear alternate-reverse;
          will-change: clip-path, transform;
        }
        .glitch-layer-b { 
          animation: glitch-anim-2 ${animSpeedB} infinite linear alternate-reverse;
          will-change: clip-path, transform;
        }
        
        @keyframes glitch-anim-1 {
          0% { clip-path: inset(20% 0 80% 0); transform: translate(4px, 1px); }
          20% { clip-path: inset(60% 0 10% 0); transform: translate(-4px, -1px); }
          40% { clip-path: inset(40% 0 50% 0); transform: translate(4px, -2px); }
          60% { clip-path: inset(80% 0 5% 0); transform: translate(-4px, 2px); }
          80% { clip-path: inset(10% 0 70% 0); transform: translate(4px, 1px); }
          100% { clip-path: inset(30% 0 50% 0); transform: translate(-4px, -1px); }
        }
        @keyframes glitch-anim-2 {
          0% { clip-path: inset(10% 0 60% 0); transform: translate(-4px, 1px); }
          20% { clip-path: inset(30% 0 20% 0); transform: translate(4px, -1px); }
          40% { clip-path: inset(70% 0 10% 0); transform: translate(-4px, 2px); }
          60% { clip-path: inset(20% 0 50% 0); transform: translate(4px, -2px); }
          80% { clip-path: inset(50% 0 30% 0); transform: translate(-4px, 1px); }
          100% { clip-path: inset(5% 0 80% 0); transform: translate(4px, -1px); }
        }
      `}</style>

      {/* Background Layers */}
      <div className="absolute inset-0 z-0 bg-[#050505] overflow-hidden">
        {/* Realidad */}
        <motion.div
          className={`absolute inset-0 transition-opacity ease-in-out ${transitionClass}`}
          style={{ opacity: showDigital ? 0 : 0.7 }}
        >
          <img src={realityImg} alt="" className="absolute top-0 left-0 w-[100vw] h-[100vh] object-cover object-center" />
        </motion.div>

        {/* Digital */}
        <motion.div
          className={`absolute inset-0 transition-opacity ease-in-out ${transitionClass}`}
          style={{ opacity: showDigital ? 0.7 : 0 }}
        >
          <img src={digitalImg} alt="" className="absolute top-0 left-0 w-[100vw] h-[100vh] object-cover object-center" />
        </motion.div>

        {/* Glitch R */}
        <motion.div 
          className={`glitch-layer-r absolute inset-0 pointer-events-none transition-opacity ease-in-out ${transitionClass}`} 
          style={{ opacity: showDigital ? 0.7 : 0 }}
        >
          <div className="absolute inset-0" style={{ filter: "hue-rotate(90deg) saturate(3)" }}>
            <img src={showDigital ? digitalImg : realityImg} alt="" className="absolute top-0 left-0 w-[100vw] h-[100vh] object-cover object-center" />
          </div>
        </motion.div>

        {/* Glitch B */}
        <motion.div 
          className={`glitch-layer-b absolute inset-0 pointer-events-none transition-opacity ease-in-out ${transitionClass}`} 
          style={{ opacity: showDigital ? 0.7 : 0 }}
        >
          <div className="absolute inset-0" style={{ filter: "hue-rotate(-60deg) saturate(2.5)" }}>
            <img src={showDigital ? digitalImg : realityImg} alt="" className="absolute top-0 left-0 w-[100vw] h-[100vh] object-cover object-center" />
          </div>
        </motion.div>

        <div className="absolute inset-0 z-10 pointer-events-none" style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.2) 60%, rgba(0,0,0,0.7) 100%)" }} />
        <div className="absolute inset-0 z-10 pointer-events-none" style={{ background: "radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.4) 100%)" }} />
      </div>

      {/* Hero Content */}
      <div className="relative z-20 text-center max-w-5xl flex flex-col items-center w-full">
        <motion.div className="w-full flex flex-col items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="relative flex flex-col items-center"
          >
            <div className="relative mb-6 group flex flex-col items-center justify-center min-h-[120px] md:min-h-[200px] w-full max-w-[90vw]">
            <div 
              className="absolute inset-0 bg-cyan-400 blur-[80px] rounded-full opacity-20 pointer-events-none animate-[glow-pulse_8s_ease-in-out_infinite]"
            />
            
            <h1 className="relative flex flex-col items-center justify-center w-full">
              <span className="sr-only">TurVolt - Carreras RC FPV y Sim Racing en Uruguay</span>
              <motion.img 
                src={getAssetUrl('logo.png')}
                alt="Logotipo de TurVolt - Carreras RC FPV y Sim Racing en Uruguay" 
                className={`w-full max-w-[220px] sm:max-w-[280px] md:max-w-[600px] h-auto relative z-10 select-none block transition-opacity ease-in-out ${transitionClass}`}
                style={{ filter: `drop-shadow(0 0 15px rgba(0, 243, 255, ${showDigital ? 0.8 : 0.4}))` }}
              />
            </h1>

            <motion.div className="mt-3 md:mt-5 font-racing text-cyan-400 tracking-[0.4em] sm:tracking-[1.2em] text-[8px] md:text-xs uppercase font-bold pl-[0.4em] sm:pl-[1.2em] opacity-60">
              Physical RC // Digital Control
            </motion.div>
          </div>

          <p className="text-sm sm:text-lg md:text-2xl text-gray-300 font-light max-w-3xl mx-auto mb-6 md:mb-12 leading-tight italic uppercase font-racing tracking-tighter px-2">
            LA BRECHA SE HA <span className="text-white font-black italic">CERRADO</span>.
            <span className="hidden md:block text-[9px] md:text-xs tracking-[0.2em] md:tracking-[0.3em] text-cyan-400 font-bold mt-3 md:mt-4 mb-1 md:mb-2">AUTOS REALES // CONTROL DIGITAL // PISTA FÍSICA</span>
            <span className="hidden md:block text-[8px] md:text-[10px] tracking-[0.1em] md:tracking-[0.2em] opacity-70">Simula la Realidad. Racing Underground. Precisión Profesional</span>
          </p>

          <div className="flex flex-col md:flex-row gap-3 md:gap-6 justify-center items-center w-full max-w-3xl">
            <div className="flex flex-col items-center w-full md:w-auto flex-1">
              <button 
                onClick={() => {
                  // Dispatch custom event to open login/booking
                  const event = new CustomEvent('open-booking');
                  window.dispatchEvent(event);
                }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                className="w-full px-6 md:px-10 py-4 md:py-6 bg-cyan-400 text-black font-racing hover:bg-white transition-colors duration-300 transform skew-x-[-10deg] md:skew-x-[-15deg] uppercase tracking-[0.2em] md:tracking-widest group relative overflow-hidden text-center text-sm md:text-lg shadow-[0_0_30px_rgba(0,243,255,0.4)]"
              >
                <span className="relative z-10 block transform skew-x-[10deg] md:skew-x-[15deg] font-black italic">Reservar_Ahora</span>
              </button>
              <span className="hidden md:block text-[8px] md:text-[9px] text-cyan-400 font-bold uppercase tracking-widest mt-2 animate-pulse">Asegura tu lugar en la pista</span>
            </div>

            <div className="flex flex-col items-center w-full md:w-auto flex-1">
              <a 
                href="#como-funciona" 
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                className="w-full px-6 md:px-10 py-4 md:py-6 bg-transparent border-2 border-white/30 text-white font-racing hover:border-white hover:bg-white/10 transition-all duration-300 transform skew-x-[-10deg] md:skew-x-[-15deg] uppercase tracking-[0.2em] md:tracking-widest group relative overflow-hidden text-center text-sm md:text-lg"
              >
                <span className="relative z-10 block transform skew-x-[10deg] md:skew-x-[15deg] font-black italic">Ver_Protocolo</span>
              </a>
              <span className="hidden md:block text-[8px] md:text-[9px] text-gray-400 font-sans uppercase tracking-widest mt-2">¿Cómo funciona?</span>
            </div>
          </div>
        </motion.div>
        </motion.div>
      </div>

      <div className="absolute bottom-12 left-12 z-40 hidden lg:block font-racing text-[9px] text-cyan-400/30 uppercase space-y-2 border-l border-cyan-400/20 pl-4">
        <div className="flex gap-4"><span>STATUS:</span> <span className={`font-bold tracking-[0.2em] ${showDigital ? 'text-cyan-400' : 'text-green-500 animate-pulse'}`}>
          {showDigital ? 'DIGITAL_LINK_ACTIVE' : 'CONNECTED'}
        </span></div>
        <div className="flex gap-4"><span>SOURCE:</span> <span className="text-silver-300">TURVOLT_OS_vDEF</span></div>
      </div>

      <div className="absolute bottom-0 left-0 w-full h-40 bg-gradient-to-t from-black to-transparent z-10 pointer-events-none"></div>
    </section>
  );
};